import { createTheme } from "@mui/material/styles";

export function darkenColor(color, percentage) {
  const hex = (color || "#16171d").replace("#", "");
  if (hex.length !== 6) {
    return "#16171d";
  }
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - percentage / 100))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - percentage / 100))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - percentage / 100))));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export function lightenColor(color, percentage) {
  const hex = (color || "#16171d").replace("#", "");
  if (hex.length !== 6) {
    return "#eceff3";
  }
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  r = Math.min(255, Math.floor(r + (255 - r) * (percentage / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percentage / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percentage / 100)));
  return `#${r.toString(16).padStart(2, "0")}${g
    .toString(16)
    .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

export const NAV = "#16171d";
export const INK = "#1a1d23";
const INK_HOVER = "#0f1115";

export default function createAppTheme() {
  const main = INK;

  return createTheme({
    palette: {
      primary: {
        main,
        dark: darkenColor(main, 18),
        light: lightenColor(main, 28),
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#4b5563",
        dark: "#374151",
        light: "#6b7280",
        contrastText: "#ffffff",
      },
      background: {
        default: "#f4f5f7",
        paper: "#ffffff",
      },
      text: {
        primary: "#1a1d23",
        secondary: "#5c6370",
      },
      divider: "#e6e8ec",
      shell: {
        nav: NAV,
        navText: "#e8eaed",
        navMuted: "#9aa3b2",
        ink: INK,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.02em" },
      h3: { fontSize: "1.25rem", fontWeight: 600 },
      h4: { fontSize: "1.125rem", fontWeight: 600 },
      h5: { fontSize: "1rem", fontWeight: 600 },
      h6: { fontSize: "0.875rem", fontWeight: 600 },
      subtitle1: { fontSize: "0.9375rem", fontWeight: 500 },
      subtitle2: { fontSize: "0.8125rem", fontWeight: 500 },
      body1: { fontSize: "0.875rem", fontWeight: 400 },
      body2: { fontSize: "0.8125rem", fontWeight: 400 },
      button: { fontSize: "0.875rem", fontWeight: 500, textTransform: "none" },
      caption: { fontSize: "0.75rem", fontWeight: 400 },
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: "#f4f5f7",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            padding: "6px 14px",
          },
          containedPrimary: {
            backgroundColor: INK,
            color: "#fff",
            "&:hover": {
              backgroundColor: INK_HOVER,
            },
          },
          containedSuccess: {
            backgroundColor: INK,
            color: "#fff",
            "&:hover": {
              backgroundColor: INK_HOVER,
            },
          },
          outlinedPrimary: {
            borderColor: INK,
            color: INK,
            "&:hover": {
              borderColor: INK,
              backgroundColor: "rgba(26, 29, 35, 0.04)",
            },
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            boxShadow: "none",
            border: "1px solid #e6e8ec",
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          elevation1: {
            boxShadow: "none",
            border: "1px solid #e6e8ec",
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: "inherit",
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
              backgroundColor: "#fff",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: "#fff",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            "& .MuiTableCell-head": {
              fontWeight: 600,
              fontSize: "0.75rem",
              color: "#5c6370",
              backgroundColor: "#fafbfc",
            },
          },
        },
      },
    },
  });
}
